import React, { useEffect } from 'react';
import { AutocompleteField } from '../AutocompleteField/AutocompleteField';
import { IAutocompleteFieldProps } from '../AutocompleteField/types';
import { Controller, FieldError, useFormContext } from 'react-hook-form';
import { isEqual } from 'lodash';
import { EMPTY_OPTION } from './constants';
import { INDICATORS } from '~/constants/infoMessages.ts';
import { getErrorKey } from '~/helpers/utils';
import { FormHelperTextProps, SlotProps, TextFieldOwnerState, TextFieldProps } from '@mui/material';
import { TAutocompleteOption } from '~/components/AutocompleteField/types';

type TFormAutocompleteProps = Omit<
  IAutocompleteFieldProps<TAutocompleteOption>,
  'value' | 'multiple' | 'onChange'
> & {
  name: string;
  onChange?: (value: TAutocompleteOption | TAutocompleteOption[] | null) => void;
  multiple?: boolean;
  shouldSaveLabel?: boolean;
  disableFormOnChange?: boolean;
  clearValueOnReset?: boolean;
  errorTouchedOnly?: boolean;
  notNullable?: boolean;
  inputProps?: SlotProps<'input', {}, TextFieldOwnerState> & { 'data-testid'?: string };
  inputSlotProps?: TextFieldProps['slotProps'];
};

export const FormAutocomplete = ({
  disableFormOnChange,
  clearValueOnReset,
  errorTouchedOnly,
  shouldSaveLabel,
  notNullable,
  inputProps,
  inputSlotProps,
  ...props
}: TFormAutocompleteProps) => {
  const { control } = useFormContext();
  const [inputValue, setInputValue] = React.useState('');
  return (
    <Controller
      name={props.name}
      control={control}
      render={({ field: { value, ref, onChange, onBlur, ...field }, fieldState, formState }) => {
        const showError =
          (errorTouchedOnly
            ? fieldState.isTouched
            : fieldState.isTouched || formState.isSubmitted) && !!fieldState.error;
        const errorMessage: string =
          (
            fieldState?.error?.message ??
            (fieldState?.error as unknown as { label: FieldError } | undefined)?.label?.message
          )?.toString() || '';

        const errorText = showError ? errorMessage : '';
        const errorDataTestId = [`${field.name}`, getErrorKey(errorText)].filter(Boolean).join('-');

        const fieldValue = Array.isArray(value)
          ? props.multiple
            ? value || []
            : value?.[0] || null
          : value;

        const handleChange = (newValue: TAutocompleteOption | TAutocompleteOption[] | null) => {
          if (isEqual(value, newValue)) return;
          !disableFormOnChange && onChange(notNullable ? newValue || EMPTY_OPTION : newValue);
          props.onChange?.(notNullable ? newValue || EMPTY_OPTION : newValue);
          if (!newValue || (Array.isArray(newValue) && newValue.length === 0)) {
            setInputValue('');
          } else {
            setInputValue(Array.isArray(newValue) ? '' : (newValue?.label ?? ''));
          }
        };

        const handleBlur = () => {
          onBlur();
          props.onBlur?.();
          const isInList = props.options?.some((option) => option?.label?.includes(inputValue));
          const invokeOnChange = shouldSaveLabel && inputValue && !isInList;
          invokeOnChange && handleChange({ label: inputValue, value: '-1' });
        };

        useEffect(() => {
          setInputValue(props.multiple ? '' : (fieldValue?.label ?? ''));
        }, [fieldValue, props.multiple]);

        const noOptionsText =
          shouldSaveLabel && inputValue
            ? INDICATORS.ADD_YOUR_OWN_OPTION
            : (props.noOptionsText ?? INDICATORS.OPTION_ABSENT);

        return (
          //@ts-ignore
          <AutocompleteField
            {...field}
            {...props}
            inputSlotProps={{
              ...inputSlotProps,
              htmlInput: {
                ...inputProps,
                ...inputSlotProps?.htmlInput,
                'data-testid': `${field.name}`,
              },
              formHelperText: {
                'data-testid': errorDataTestId,
              } as FormHelperTextProps & { 'data-testid': string },
            }}
            noOptionsText={noOptionsText}
            onInputChange={(_, newInputValue, reason) => {
              if (reason === 'input') {
                setInputValue(newInputValue);
              }
              if (reason === 'clear') {
                setInputValue('');
              }
            }}
            inputValue={inputValue}
            inputRef={ref}
            onChange={handleChange}
            onBlur={handleBlur}
            value={fieldValue ?? (props.multiple ? [] : null)}
            error={!!showError}
            helperText={errorMessage}
          />
        );
      }}
    />
  );
};

export default FormAutocomplete;
